import { useCallback, useMemo, useState } from 'react';

import { type UnifiedTransfer } from '@column/api';

import { type ReviewTargets } from '~/lib/transfers/hooks/useCreateReviewBatch';
import { isBatchTransfer } from '~/lib/transfers/utils';

export type SelectionState = boolean | 'indeterminate';

const toggle = (set: ReadonlySet<string>, id: string, selected: boolean): ReadonlySet<string> => {
  const next = new Set(set);
  if (selected) next.add(id);
  else next.delete(id);
  return next;
};

export const useApprovalSelection = () => {
  const [transferIds, setTransferIds] = useState<ReadonlySet<string>>(() => new Set());
  const [batches, setBatches] = useState<ReadonlyMap<string, number>>(() => new Map());

  const rowState = useCallback(
    (row: UnifiedTransfer): SelectionState => (isBatchTransfer(row) ? batches.has(row.id) : transferIds.has(row.id)),
    [batches, transferIds]
  );

  const setRowsSelected = useCallback((rows: UnifiedTransfer[], selected: boolean) => {
    setBatches((prev) => {
      const next = new Map(prev);
      rows.filter(isBatchTransfer).forEach((row) => {
        if (!selected) next.delete(row.id);
        else next.set(row.id, row.batch_transfer_count ?? 0);
      });
      return next;
    });
    setTransferIds((prev) =>
      rows
        .filter((row) => !isBatchTransfer(row))
        .reduce<ReadonlySet<string>>((next, row) => toggle(next, row.id, selected), prev)
    );
  }, []);

  const clear = useCallback(() => {
    setTransferIds(new Set());
    setBatches(new Map());
  }, []);

  const transferCount = useMemo(
    () => [...batches.values()].reduce((sum, memberCount) => sum + memberCount, transferIds.size),
    [batches, transferIds]
  );

  const targets = useMemo<ReviewTargets>(
    () => ({ transfer_ids: [...transferIds], ach_transfer_batch_ids: [...batches.keys()] }),
    [batches, transferIds]
  );

  return useMemo(
    () => ({
      clear,
      isEmpty: transferIds.size === 0 && batches.size === 0,
      rowState,
      setRowsSelected,
      targets,
      transferCount,
    }),
    [batches, clear, rowState, setRowsSelected, targets, transferCount, transferIds]
  );
};
